import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { AnimatedSection } from '../components/AnimatedSection';

const values = [
  { title: 'Clear Communication', desc: 'We keep things straightforward and transparent throughout every project.' },
  { title: 'Strong Visual Storytelling', desc: 'Every design decision is made to communicate your brand\'s message effectively.' },
  { title: 'High-Quality Execution', desc: 'We never ship something we\'re not proud of. Quality is non-negotiable.' },
  { title: 'Client-Focused Approach', desc: 'We invest in relationships and understanding your unique needs.' },
];

const founderStats = [
  { value: '15+', label: 'Projects Completed' },
  { value: '2025', label: 'Established' },
  { value: '2', label: 'Founders' },
];

export default function About() {
  return (
    <div data-testid="about-page" style={{ paddingTop: 64, background: 'var(--canvas)' }}>
      {/* Hero */}
      <section style={{ borderBottom: '1px solid var(--border)', paddingTop: 80, paddingBottom: 80 }}>
        <div className="container-site">
          <AnimatedSection>
            <span className="label-sm">Our Story</span>
            <h1 className="heading-xl" style={{ fontSize: 'clamp(40px, 8vw, 88px)', marginTop: 20 }}>
              Driven by Creativity.<br />Powered by <span style={{ color: 'var(--accent)' }}>Motion.</span>
            </h1>
            <p className="body-base" style={{ maxWidth: 600, marginTop: 24, fontSize: 16 }}>
              Outbox Thinking was founded by Ganesh and Hemant with a clear vision — to create impactful visual experiences through motion design and packaging solutions.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* About Content */}
      <section data-testid="about-content-section" style={{ borderBottom: '1px solid var(--border)', background: 'var(--canvas)' }}>
        <div className="container-site" style={{ paddingTop: 80, paddingBottom: 80 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 64, alignItems: 'start' }}>
            <AnimatedSection>
              <span className="label-sm">Who We Are</span>
              <h2 className="heading-lg" style={{ fontSize: 'clamp(24px, 3.5vw, 40px)', margin: '12px 0 24px' }}>Our Journey</h2>
              <p className="body-base" style={{ marginBottom: 20, lineHeight: 1.8 }}>
                We started our journey last year and have successfully completed 15+ projects across 2D animation and packaging design.
              </p>
              <p className="body-base" style={{ marginBottom: 24, lineHeight: 1.8 }}>
                We are now expanding our operations to work with more brands and deliver stronger creative impact.
              </p>
              
              {/* Stats */}
              <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', marginTop: 32 }}>
                {founderStats.map((stat, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="heading-lg" style={{ fontSize: 'clamp(28px, 4vw, 44px)', color: 'var(--ink-950)' }}>{stat.value}</div>
                    <div className="label-sm" style={{ marginBottom: 0, color: 'var(--ink-500)', marginTop: 6 }}>{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>

            {/* Founders */}
            <AnimatedSection delay={0.15}>
              <span className="label-sm">Founders</span>
              <h2 className="heading-lg" style={{ fontSize: 'clamp(24px, 3.5vw, 40px)', margin: '12px 0 24px' }}>Ganesh & Hemant</h2>
              <p className="body-base" style={{ marginBottom: 16, lineHeight: 1.8 }}>
                Together, we bring a shared passion for design and a commitment to helping brands communicate clearly through visual storytelling.
              </p>
              
              {/* Founder card */}
              <div style={{ marginTop: 32, padding: 28, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--ink-950)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: 'var(--canvas)', fontSize: 14, fontFamily: 'Space Grotesk', fontWeight: 700 }}>G</span>
                    </div>
                    <div>
                      <p style={{ fontFamily: 'Space Grotesk', fontSize: 15, fontWeight: 600, color: 'var(--ink-950)' }}>Ganesh</p>
                      <p className="label-sm" style={{ marginBottom: 0, color: 'var(--ink-500)' }}>Co-Founder</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--ink-950)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: 'var(--canvas)', fontSize: 14, fontFamily: 'Space Grotesk', fontWeight: 700 }}>H</span>
                    </div>
                    <div>
                      <p style={{ fontFamily: 'Space Grotesk', fontSize: 15, fontWeight: 600, color: 'var(--ink-950)' }}>Hemant</p>
                      <p className="label-sm" style={{ marginBottom: 0, color: 'var(--ink-500)' }}>Co-Founder</p>
                    </div>
                  </div>
                </div>
                <div style={{ height: 1, background: 'var(--border)', margin: '20px 0' }} />
                <p className="font-mono" style={{ fontSize: 10, color: 'var(--accent)', letterSpacing: '0.08em' }}>
                  CURRENTLY EXPANDING
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Our Focus / Values */}
      <section data-testid="focus-section" style={{ background: 'var(--surface)' }}>
        <div className="container-site" style={{ paddingTop: 80, paddingBottom: 80 }}>
          <AnimatedSection style={{ marginBottom: 48 }}>
            <span className="label-sm">Our Focus</span>
            <h2 className="heading-lg" style={{ fontSize: 'clamp(28px, 4vw, 48px)', marginTop: 12 }}>What Drives Us</h2>
          </AnimatedSection>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 24 }}>
            {values.map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                style={{ padding: 28, background: 'var(--canvas)', border: '1px solid var(--border)', borderRadius: 8 }}
              >
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--ink-950)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <span className="font-mono" style={{ color: 'var(--canvas)', fontSize: 12 }}>{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="heading-md" style={{ fontSize: 17, marginBottom: 8 }}>{v.title}</h3>
                <p className="body-base" style={{ fontSize: 13 }}>{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
